/**
 * Product Search Functions
 *
 * Standard functions for searching products.
 * Calls the internal Algolia search directly (no HTTP round-trip).
 *
 * Previously these went through the BFF API endpoint (/api/products/search)
 * which caused unnecessary server-to-self HTTP round-trips on Vercel.
 *
 * @example
 * ```ts
 * const result = await searchProducts({
 *   locale: 'us-en',
 *   filters: { collection: ['navitimer'] },
 * });
 * ```
 */

import { devLog, devWarn } from '@/lib/utils/devLog';
import { searchProductsInternal } from './search';
import type { ProductHit, SearchParams, SearchResult, StrapHit, WatchHit } from './types';
import type { FacetResults } from './types';

/**
 * Product types for search.
 */
export type SearchProductType = 'Watch' | 'Strap' | 'Eyewear';

/**
 * Extended search parameters with product type.
 */
export interface SearchByTypeParams extends SearchParams {
  /** Product type to search for */
  productType?: SearchProductType;

  /**
   * Product configurator timestamp context.
   * Use `'search'` on the search page / overlay so SKU queries match Breitling behavior.
   */
  filterContext?: 'plp' | 'search' | 'components';

  /**
   * Pre-fetched facet values for ID translation.
   * If provided, avoids an extra facet fetch.
   * Used when the caller (e.g., orchestrator) already has cached facets.
   */
  facetLookup?: FacetResults;
}

/**
 * Search for products by type.
 * This is the unified search function that handles all product types.
 *
 * @param params - Search parameters including product type
 * @returns Search result with hits, facets, and pagination
 */
export async function searchByProductType(
  params: SearchByTypeParams
): Promise<SearchResult<ProductHit>> {
  const productType = params.productType || 'Watch';

  devLog(`[searchByProductType] Searching ${productType}:`, {
    locale: params.locale,
    filters: params.filters,
    hitsPerPage: params.hitsPerPage,
  });

  try {
    const result = await searchProductsInternal({
      ...params,
      productType,
    });

    devLog(`[searchByProductType] ${productType} result:`, {
      totalHits: result.totalHits,
      hits: result.hits?.length,
    });

    return result;
  } catch (error) {
    devWarn(`[searchByProductType] Error searching ${productType}:`, error);
    return {
      hits: [],
      totalHits: 0,
      totalPages: 0,
      page: 1,
      hitsPerPage: params.hitsPerPage || 24,
      facets: {},
      processingTimeMs: 0,
    };
  }
}

/**
 * Search for watch products.
 * Convenience wrapper around searchByProductType.
 *
 * @param params - Search parameters
 * @returns Search result with hits, facets, and pagination
 */
export async function searchProducts(params: SearchParams): Promise<SearchResult<WatchHit>> {
  return searchByProductType({ ...params, productType: 'Watch' }) as Promise<
    SearchResult<WatchHit>
  >;
}

/**
 * Search for strap products.
 * Convenience wrapper around searchByProductType.
 *
 * @param params - Search parameters
 * @returns Search result with hits, facets, and pagination
 */
export async function searchStraps(params: SearchParams): Promise<SearchResult<StrapHit>> {
  return searchByProductType({ ...params, productType: 'Strap' }) as Promise<
    SearchResult<StrapHit>
  >;
}
